import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NotificationsService } from '@app/core';
import { NewsletterService } from '@app/domains/newsletter/services';
import { SharedModule } from '@app/shared';
import {
  TuiButton,
  TuiError,
  TuiHint,
  TuiIcon,
  TuiLabel,
  TuiTextfield,
} from '@taiga-ui/core';
import {
  TuiButtonLoading,
  TuiCheckbox,
  TuiFieldErrorPipe,
  TuiInputNumber,
  TuiTooltip,
} from '@taiga-ui/kit';
import { CertificateService } from '../../services';

@Component({
  selector: 'app-android-keystore-generator-form-section',
  imports: [
    SharedModule,
    TuiButton,
    TuiButtonLoading,
    TuiCheckbox,
    TuiError,
    TuiFieldErrorPipe,
    TuiHint,
    TuiIcon,
    TuiInputNumber,
    TuiLabel,
    TuiTextfield,
    TuiTooltip,
  ],
  providers: [CertificateService],
  templateUrl: './android-keystore-generator-form-section.component.html',
  styleUrl: './android-keystore-generator-form-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AndroidKeystoreGeneratorFormSectionComponent {
  protected readonly formGroup = new FormGroup({
    fullName: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    organization: new FormControl(''),
    organizationalUnit: new FormControl(''),
    city: new FormControl(''),
    state: new FormControl(''),
    country: new FormControl('', [Validators.maxLength(2)]),
    password: new FormControl('', [
      Validators.required,
      Validators.minLength(6),
    ]),
    keyAlias: new FormControl('', [Validators.required]),
    validityYears: new FormControl<number | null>(25, [
      Validators.required,
      Validators.min(1),
    ]),
    newsletter: new FormControl(true),
    privacy: new FormControl(false, [Validators.requiredTrue]),
  });
  protected readonly loading = signal(false);
  protected readonly subscribeNewsletter = this.newsletterService.subscribe();

  constructor(
    private readonly certificateService: CertificateService,
    private readonly newsletterService: NewsletterService,
    private readonly notificationsService: NotificationsService,
  ) {
    this.formGroup.controls.newsletter.valueChanges.subscribe(value => {
      const privacyControl = this.formGroup.controls.privacy;
      if (value) {
        privacyControl.setValidators([Validators.requiredTrue]);
        privacyControl.enable();
      } else {
        privacyControl.clearValidators();
        privacyControl.setValue(false);
        privacyControl.disable();
      }
      privacyControl.updateValueAndValidity();
    });
  }

  protected async onSubmit(): Promise<void> {
    this.formGroup.markAllAsTouched();
    if (this.formGroup.invalid) {
      return;
    }
    const value = this.formGroup.value;
    if (!value.fullName || !value.email || !value.password || !value.keyAlias) {
      return;
    }
    this.loading.set(true);
    try {
      if (value.newsletter) {
        try {
          await this.subscribeNewsletter.mutateAsync({ email: value.email });
        } catch {
          // Newsletter subscription is best-effort
        }
      }
      const p12 = await this.certificateService.generateAndroidKeystore({
        commonName: value.fullName,
        email: value.email,
        organization: value.organization || undefined,
        organizationalUnit: value.organizationalUnit || undefined,
        locality: value.city || undefined,
        state: value.state || undefined,
        country: value.country || undefined,
        password: value.password,
        keyAlias: value.keyAlias,
        validityYears: value.validityYears ?? 25,
      });
      this.certificateService.downloadFile(
        p12,
        `${value.keyAlias}.p12`,
        'application/x-pkcs12',
      );
      void this.notificationsService.showSuccessNotification(
        'The keystore was generated successfully.',
      );
    } finally {
      this.loading.set(false);
    }
  }
}
